import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const LanguageSection = ({ onUpdate, boostInput = false }) => {
  const { fetchCachedList } = useBuild();
  const [currentLanguage, setCurrentLanguage] = useState('');
  const [boost, setBoost] = useState(1);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch language options on component mount
  useEffect(() => {
    const fetchLanguageOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const languagesData = await fetchCachedList('/search/frequent_values/farcaster-items/lang');
        setLanguageOptions(languagesData);
      } catch (err) {
        console.error('Failed to fetch language options:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLanguageOptions();
  }, [fetchCachedList]);

  const handleLanguageAction = (actionType) => {
    if (!currentLanguage) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: 'lang',
      value: [currentLanguage]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    setCurrentLanguage(''); // Reset after adding
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">
          Failed to load languages: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Language</InputLabel>
        <Select
          value={currentLanguage}
          label="Language"
          onChange={(e) => setCurrentLanguage(e.target.value)}
          disabled={loading}
        >
          {languageOptions.map((language) => (
            <MenuItem key={language.id} value={language.id}>
              {language.id} ({language.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
          disabled={loading}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleLanguageAction('include')}
          disabled={!currentLanguage || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleLanguageAction('boost')}
            disabled={!currentLanguage || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleLanguageAction('exclude')}
          disabled={!currentLanguage || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default LanguageSection;

